import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import mapboxgl from 'mapbox-gl';
import { loadTSOZones } from '../dataLoader';
import { addTSOZoneLayers, updateLayerTheme } from '../layers';
import { setupMapEventHandlers } from '../eventHandlers';
import type { MarketZoneId, MarketZone } from '@app/common/static-data/energy-markets';

interface UseMapInitializationReturn {
  mapContainerRef: React.RefObject<HTMLDivElement>;
  mapRef: React.MutableRefObject<mapboxgl.Map | null>;
  zonesLoaded: boolean;
  zonesReloading: boolean;
  zoneFeatureMapRef: React.MutableRefObject<Map<string, number[]>>;
  zoneCentersRef: React.MutableRefObject<Map<string, [number, number]>>;
  selectedZones: Set<string>;
  centerOnZone: (zoneId: string, animate?: boolean) => void;
  setHandleZoneSelect: (handler: (zoneId: string) => void) => void;
}

interface UseMapInitializationProps {
  theme: string;
  selectedZones: MarketZoneId[];
  activeTab: 'list' | 'map';
  availableZones?: MarketZone[];
  zonesReady?: boolean;
}

export const useMapInitialization = ({
  theme,
  selectedZones: selectedZonesArray,
  activeTab,
  availableZones,
  zonesReady = true,
}: UseMapInitializationProps): UseMapInitializationReturn => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const [zonesLoaded, setZonesLoaded] = useState(false);
  const [zonesReloading, setZonesReloading] = useState(false);
  const zoneFeatureMapRef = useRef<Map<string, number[]>>(new Map());
  const zoneCentersRef = useRef<Map<string, [number, number]>>(new Map());
  const [handleZoneSelectFn, setHandleZoneSelectFn] = useState<((zoneId: string) => void) | null>(
    null,
  );

  // Use provided selectedZones array as Set for map feature state management (memoized)
  const zonesSignature = useMemo(() => (selectedZonesArray || []).join('|'), [selectedZonesArray]);
  const selectedZones = useMemo(() => new Set<string>(selectedZonesArray || []), [zonesSignature]);

  // Setter for the zone select handler
  const setHandleZoneSelect = useCallback((handler: (zoneId: string) => void) => {
    setHandleZoneSelectFn(() => handler);
  }, []);

  // Function to center map on a specific zone
  const centerOnZone = useCallback(
    (zoneId: string, animate: boolean = true) => {
      if (!mapRef.current || !zonesLoaded) return;

      const center = zoneCentersRef.current.get(zoneId);
      if (center) {
        if (animate) {
          mapRef.current.flyTo({
            center: center,
            zoom: 3.5,
            essential: true,
            duration: 1000, // Smooth 1-second animation
          });
        } else {
          mapRef.current.jumpTo({
            center: center,
            zoom: 3.5,
          });
        }
      }
    },
    [zonesLoaded],
  );

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current || !zonesReady) return;

    mapboxgl.accessToken =
      'pk.eyJ1IjoibWFydmluZ2FibGVyIiwiYSI6ImNtODRpaW55NjBzdjcyanM3a2l3bDAxYXAifQ.3zrJdRYQt-lD-cP8phh1dw';

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style:
        theme === 'dark' ? 'mapbox://styles/mapbox/dark-v11' : 'mapbox://styles/mapbox/light-v11',
      center: [10.0, 54.0], // Centered on Europe
      zoom: 3.5,
      attributionControl: false,
    });

    map.addControl(
      new mapboxgl.AttributionControl({
        customAttribution: ['© Jua'],
      }),
    );

    mapRef.current = map;

    map.on('load', async () => {
      // Add navigation controls
      map.addControl(new mapboxgl.NavigationControl(), 'top-right');

      try {
        // Load TSO zones data with center coordinates (filtered by available zones)
        const { combinedGeoJSON, featureMap, zoneCenters, zoneBounds } =
          await loadTSOZones(availableZones);

        // Store the zone-to-feature mapping and center coordinates
        zoneFeatureMapRef.current = featureMap;
        zoneCentersRef.current = zoneCenters;

        // Add layers to map
        addTSOZoneLayers(map, combinedGeoJSON, theme);

        // Immediately apply current selections on first load
        try {
          if (selectedZones.size > 0) {
            selectedZones.forEach((zoneId) => {
              const featureIds = zoneFeatureMapRef.current.get(zoneId);
              if (featureIds) {
                featureIds.forEach((id: number) => {
                  map.setFeatureState({ source: 'tso-zones', id }, { selected: true });
                });
              }
            });
          }
        } catch (e) {
          // Silently ignore if style not yet ready; effects will reapply
          void e;
        }

        // Setup event handlers
        setupMapEventHandlers(
          map,
          zoneFeatureMapRef,
          selectedZones,
          () => {},
          handleZoneSelectFn || (() => {}),
        );

        setZonesLoaded(true);

        // If there are selected zones, fit bounds to all their extents
        if (selectedZonesArray && selectedZonesArray.length > 0 && activeTab === 'map') {
          const boundsArr = selectedZonesArray.map((z) => zoneBounds.get(z)).filter(Boolean) as [
            [number, number],
            [number, number],
          ][];
          if (boundsArr.length > 0) {
            let minLng = Infinity,
              minLat = Infinity,
              maxLng = -Infinity,
              maxLat = -Infinity;
            boundsArr.forEach(([[minx, miny], [maxx, maxy]]) => {
              if (minx < minLng) minLng = minx;
              if (miny < minLat) minLat = miny;
              if (maxx > maxLng) maxLng = maxx;
              if (maxy > maxLat) maxLat = maxy;
            });
            if (isFinite(minLng) && isFinite(minLat) && isFinite(maxLng) && isFinite(maxLat)) {
              const degPad = 0.25;
              map.fitBounds(
                [
                  [minLng - degPad, minLat - degPad],
                  [maxLng + degPad, maxLat + degPad],
                ],
                { padding: 60, duration: 700, essential: true },
              );
            }
          } else {
            setTimeout(() => centerOnZone(selectedZonesArray[0], false), 500);
          }
        }
      } catch (error) {
        console.error('Failed to initialize map layers:', error);
      }
    });

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [theme, zonesReady]);

  // Update zone colors when theme changes
  useEffect(() => {
    if (mapRef.current && zonesLoaded) {
      updateLayerTheme(mapRef.current, theme);
    }
  }, [theme, zonesLoaded]);

  // Update event handlers when zone select function changes
  useEffect(() => {
    if (mapRef.current && zonesLoaded && handleZoneSelectFn) {
      // Remove existing handlers and re-setup with new handler
      const map = mapRef.current;
      map.off('click', 'tso-zones-fill');
      map.off('mouseenter', 'tso-zones-fill');
      map.off('mouseleave', 'tso-zones-fill');

      // Re-setup event handlers with new function
      setupMapEventHandlers(map, zoneFeatureMapRef, selectedZones, () => {}, handleZoneSelectFn);
    }
  }, [handleZoneSelectFn, zonesLoaded]);

  // Track previous availableZones to reload when they change after initial load
  const prevAvailableZonesRef = useRef<MarketZone[] | undefined>();

  // Reload zones when availableZones changes after initial load
  useEffect(() => {
    // Only reload if map is fully loaded and style is ready
    if (!mapRef.current || !mapRef.current.isStyleLoaded() || !zonesLoaded) return;

    // Skip if zones haven't actually changed (deep comparison)
    const currentZoneIds =
      availableZones
        ?.map((z) => z.id)
        .sort()
        .join(',') || 'all';
    const prevZoneIds =
      prevAvailableZonesRef.current
        ?.map((z) => z.id)
        .sort()
        .join(',') || 'all';

    if (currentZoneIds === prevZoneIds) return;

    // Skip the first change (initial load) - we only want to reload on subsequent changes
    if (prevAvailableZonesRef.current === undefined) {
      prevAvailableZonesRef.current = availableZones;
      return;
    }

    // Reload zones for variable changes
    const reloadZones = async () => {
      const map = mapRef.current;
      if (!map) return;

      setZonesReloading(true);

      try {
        // Remove existing layers and source safely
        try {
          if (map.getLayer('tso-zones-border')) {
            map.removeLayer('tso-zones-border');
          }
        } catch {
          // Layer might not exist, ignore
        }

        try {
          if (map.getLayer('tso-zones-fill')) {
            map.removeLayer('tso-zones-fill');
          }
        } catch {
          // Layer might not exist, ignore
        }

        try {
          if (map.getSource('tso-zones')) {
            map.removeSource('tso-zones');
          }
        } catch {
          // Source might not exist, ignore
        }

        // Wait a bit to ensure cleanup is complete
        await new Promise((resolve) => setTimeout(resolve, 50));

        // Reload zones with new filtering
        const { combinedGeoJSON, featureMap, zoneCenters } = await loadTSOZones(availableZones);

        // Update refs
        zoneFeatureMapRef.current = featureMap;
        zoneCentersRef.current = zoneCenters;

        // Re-add layers
        addTSOZoneLayers(map, combinedGeoJSON, theme);

        // Small delay to ensure everything is properly loaded before reapplying selections
        setTimeout(() => {
          // Reapply current selections to new features
          selectedZones.forEach((zoneId) => {
            const featureIds = zoneFeatureMapRef.current.get(zoneId);
            if (featureIds) {
              featureIds.forEach((id: number) => {
                try {
                  map.setFeatureState({ source: 'tso-zones', id }, { selected: true });
                } catch {
                  // Feature might not exist yet, ignore
                }
              });
            }
          });
        }, 100);
      } catch (error) {
        console.error('Failed to reload zones:', error);
      } finally {
        setZonesReloading(false);
      }
    };

    prevAvailableZonesRef.current = availableZones;
    reloadZones();
  }, [availableZones, theme, selectedZones, zonesLoaded]);

  return {
    mapContainerRef,
    mapRef,
    zonesLoaded,
    zonesReloading,
    zoneFeatureMapRef,
    zoneCentersRef,
    selectedZones,
    centerOnZone,
    setHandleZoneSelect,
  };
};
